import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { AddUserIcon, SyncIcon, UserFourIcon, UserRemoveIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { diffuseTravelInformation } from '../../../api/affected-staffs/diffusion-actions'
import {
  diffuseCourseLocation,
  diffuseHostFamilyAccommodation,
  diffuseTeam
} from '../../../api/affected-staffs/diffusion-actions'
import { getAffectedStaff } from '../../../api/affected-staffs/get-affected-staff'
import { getAffectedStaffRadParticipant } from '../../../api/affected-staffs/get-affected-staff-rad-participant'
import { updateAffectedStaff } from '../../../api/affected-staffs/update-affected-staff'
import { updateAffectedStaffStatus } from '../../../api/affected-staffs/update-affected-staff-status'
import { API } from '../../../api/apis.config'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import AffectedStaffViewForm from '../../../components/pages/affected-staffs/affect-staff-form/affected-staff-view-form'
import CancelAffectedStaffModal from '../../../components/pages/affected-staffs/cancel-affected-staff/cancel-affected-staff-modal'
import ReassignStaffRequest from '../../../components/pages/affected-staffs/reassign-staff-request'
import { EmailModal } from '../../../components/pages/common/email-modal'
import { Paths } from '../../../lib/constants/path'
import { useObserver } from '../../../lib/contexts/observer'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { twoButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError, toastDiffusionResult } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapAffectedStaffToAffectedStaffInput } from './utils'

const AffectedStaffViewPage = () => {
  const { id } = useParams()
  const { back } = useNavigator()
  const { modalState, toggleModal, closeModel } = useModalManager<
    'staff-requests' | 'send-reassignment-email' | 'deaffect' | 'send-deaffection-email'
  >(['staff-requests', 'send-reassignment-email', 'deaffect', 'send-deaffection-email'])

  const observer = useObserver({ key: 'affected-staff-view' })

  // the email modal calls closeModal twice on send, so we guard against navigating back twice
  const hasLeftPageRef = useRef(false)
  const leavePage = () => {
    if (hasLeftPageRef.current) return
    hasLeftPageRef.current = true
    back(Paths.AFFECTED_STAFFS_LIST_PAGE)
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['affected-staff-view', id],
    queryFn: () => {
      if (id && !isNaN(+id)) return getAffectedStaff(+id, 'FULL')
      return null
    },
    enabled: Boolean(id && !isNaN(+id))
  })

  const {
    data: radParticipant,
    isPending: isRadParticipantPending,
    refetch: refetchRadParticipant
  } = useQuery({
    queryKey: ['affected-staff-participated-in-rad', id],
    queryFn: () => {
      if (id && !isNaN(+id)) return getAffectedStaffRadParticipant(+id)
      return null
    },
    enabled: Boolean(id && !isNaN(+id) && data?.staffRequest.centers.length)
  })

  const isCurrentCenterLocked = data?.staffRequest.centers.some((center) => center.locked) ?? false

  const updateAffectedStaffMutation = useMutation({
    mutationFn: updateAffectedStaff,
    onError: showAxiosError,
    onSuccess: async () => {
      toast.success('Mise à jour réussie')
      await Promise.all([
        refetch(),
        ...(data?.staffRequest.centers.length ? [refetchRadParticipant()] : [])
      ])
      observer.notify(['update-affected-staff-data'])
    }
  })

  const updateAffectedStaffStatusMutation = useMutation({
    mutationFn: updateAffectedStaffStatus,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Mise à jour du statut réussie')
      refetch()
      observer.notify(['update-affected-staff-data'])
    }
  })

  const diffuseTravelInformationMutation = useMutation({
    mutationFn: diffuseTravelInformation,
    onError: showAxiosError,
    onSuccess: (result) => {
      toastDiffusionResult(result)
      refetch()
    }
  })

  const diffuseHostFamilyAccommodationMutation = useMutation({
    mutationFn: diffuseHostFamilyAccommodation,
    onError: showAxiosError,
    onSuccess: (result) => {
      toastDiffusionResult(result)
      refetch()
    }
  })

  const diffuseTeamMutation = useMutation({
    mutationFn: diffuseTeam,
    onError: showAxiosError,
    onSuccess: (result) => {
      toastDiffusionResult(result)
      refetch()
    }
  })

  const diffuseCourseLocationMutation = useMutation({
    mutationFn: diffuseCourseLocation,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Diffusion réussie')
      refetch()
    }
  })

  if (isLoading) return <LoadingScreen />
  if (!data || !id) return <NotFoundPage />
  if (data.staffRequest.centers.length > 0 && isRadParticipantPending) return <LoadingScreen />

  return (
    <div>
      <UserNavBar title="Suivi du poste" icon={UserFourIcon} />
      <div className="h-4" />
      <Box className=" flex flex-col gap-4">
        <div className="flex gap-3 justify-between w-full">
          <div className="flex gap-2 items-center">
            <Button
              variant="ghost"
              loading={diffuseTravelInformationMutation.isPending}
              onClick={() => diffuseTravelInformationMutation.mutateAsync(+id)}
              label="Diffuser le voyage"
              disabled={data.travelInformationDiffused || !data.trip}
            />
            <Button
              variant="ghost"
              disabled={data.courseLocationDiffused}
              loading={diffuseCourseLocationMutation.isPending}
              onClick={() => diffuseCourseLocationMutation.mutateAsync(+id)}
              label="Diffuser OL Infos"
            />
            <Button
              variant="ghost"
              disabled={data.hostFamilyAccommodationDiffused}
              loading={diffuseHostFamilyAccommodationMutation.isPending}
              onClick={() => diffuseHostFamilyAccommodationMutation.mutateAsync(+id)}
              label="Diffuser info hébergement"
            />
            <Button
              variant="ghost"
              disabled={data.teamDiffused}
              loading={diffuseTeamMutation.isPending}
              onClick={() => diffuseTeamMutation.mutateAsync(+id)}
              label="Diffuser l'équipe"
            />
          </div>
          <div className="flex gap-2 items-center">
            <InnerBox className="p-2 ">
              <Checkbox
                checked={data.status === AffectedStaffStatusEnum.PENDING}
                label={'A confirmer'}
                onChange={(checked) => {
                  if (checked)
                    updateAffectedStaffStatusMutation.mutateAsync({
                      id: +id,
                      status: AffectedStaffStatusEnum.PENDING
                    })
                  else
                    updateAffectedStaffStatusMutation.mutateAsync({
                      id: +id,
                      status: AffectedStaffStatusEnum.AFFECTED
                    })
                }}
              />
            </InnerBox>
            <Button
              variant="primary"
              icon={SyncIcon}
              iconSize={14}
              onClick={() => toggleModal('staff-requests')}
              label="Affecter à un autre centre similaire"
            />
            {data.status !== AffectedStaffStatusEnum.CANCELLED && (
              <Button
                variant="destructive"
                icon={UserRemoveIcon}
                iconSize={14}
                onClick={() => toggleModal('deaffect')}
                label="Désaffecter"
              />
            )}
          </div>
        </div>

        <AffectedStaffViewForm
          key={JSON.stringify({ data, radParticipant })}
          affectedStaffId={data.id}
          defaultValues={mapAffectedStaffToAffectedStaffInput(data, {
            participatedInRad: radParticipant?.partcipatedInRad
          })}
          buttonsConfig={twoButtonsConfig({
            cancelText: 'Fermer',
            onCancel: () => back(),
            onConfirm: async ({ status: _status, ...values }) => {
              await updateAffectedStaffMutation.mutateAsync({
                id: data.id,
                staffId: data.staffId,
                staffRequestId: data.staffRequestId,
                ...values
              })
            }
          })}
        />
      </Box>

      <Modal
        isOpen={modalState['staff-requests']}
        title={'Affecter à un autre centre similaire'}
        icon={AddUserIcon}
        size="large"
        hideActions
        onCancel={() => closeModel('staff-requests')}
      >
        <ReassignStaffRequest
          className={MODAL_CONTENT_CLASSNAME}
          affectedStaffId={+id}
          staffId={data.staffId}
          endDate={data.endDate}
          onSuccess={() => {
            refetch()
            if (isCurrentCenterLocked) toggleModal('send-reassignment-email')
          }}
          onCancel={() => closeModel('staff-requests')}
          filters={{
            countries: data.staffRequest.country ? [data.staffRequest.country.name] : undefined,
            startDate: data.staffRequest.startDate ?? undefined,
            endDate: data.staffRequest.endDate ?? undefined,
            formulas: data.staffRequest.formulas?.map((formula) => formula.id) ?? undefined,
            excludedStaffRequestId: data.staffRequestId,
            includeCenter: true
          }}
        />
      </Modal>

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffReassignmentNotification.url}
        label="Vous avez changé le centre d'un encadrant précédemment affecté à un centre verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-reassignment-email']}
        closeModal={() => closeModel('send-reassignment-email')}
      />

      <CancelAffectedStaffModal
        affectedStaffIds={[data.id]}
        isOpen={modalState.deaffect}
        onCancel={() => closeModel('deaffect')}
        onSuccess={() => {
          // the affected staff is cancelled, so we leave the page (after the notification when the center is locked)
          if (isCurrentCenterLocked) toggleModal('send-deaffection-email')
          else leavePage()
        }}
      />

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffDeaffectionNotification.url}
        label="Vous vous apprêtez à désaffecter un encadrant sur un centre verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-deaffection-email']}
        closeModal={() => {
          closeModel('send-deaffection-email')
          leavePage()
        }}
      />
    </div>
  )
}

export default AffectedStaffViewPage
